import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Eye, Trash2 } from "lucide-react";
import { useApproveDraft, useDiscardDraft, useSubmitDraft } from "@/api/mutations/repoLifecycle";
import { repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import { DraftChangesDialog } from "@/components/repo-lifecycle/DraftChangesDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";

// #293 (UC-3/UC-4): EINE Zeile für „eine Änderung wartet auf Entscheidung" — genutzt für
// Katalog-Änderungen (Marketplace-Detail) und Vorlagen-Änderungen (Vorlagen-Detail).
// Fach-Vokabular: Titel + Zustand + von wem + Freigeben/Verwerfen/Änderungen ansehen.
// KEIN Branch, KEIN Ref — die Lifecycle-Koordinaten (repoId+branch) sind reine Adresse.
export function PendingChangeRow({
  repoId,
  branch,
  title,
  state,
  createdBy,
  canApprove,
  discardDescription,
  onDecided,
  testId,
}: {
  repoId: string;
  branch: string;
  title: string;
  state: "draft" | "submitted";
  createdBy: string | null;
  canApprove: boolean;
  /** Kontext-spezifischer Verwerfen-Text (nennt die Folgen fürs Fachobjekt). */
  discardDescription: string;
  /** nach Freigeben/Verwerfen (z.B. Verkettungs-Dialog öffnen, refetch). */
  onDecided?: (aktion: "freigegeben" | "verworfen") => void;
  testId?: string;
}) {
  const approve = useApproveDraft();
  const discard = useDiscardDraft();
  const submit = useSubmitDraft();
  const [changesOpen, setChangesOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  return (
    <li className="flex items-center gap-3 rounded-md border border-border px-2.5 py-2" data-testid={testId}>
      <span className="text-[13px] font-medium text-text">{title}</span>
      <Badge variant="busy">{state === "submitted" ? "wartet auf Freigabe" : "noch nicht eingereicht"}</Badge>
      {createdBy && <span className="text-[11.5px] text-text3">von {createdBy}</span>}
      <span className="ml-auto flex items-center gap-1.5">
        <Button variant="outline" size="sm" onClick={() => setChangesOpen(true)} data-testid={testId ? `${testId}-changes` : undefined}>
          <Eye className="h-3.5 w-3.5" /> Änderungen ansehen
        </Button>
        {/* #293 (Review M2): auch „Einreichen" ist eine FACH-Aktion — sie gehört in die
            Zeile, nicht hinter die eingeklappte Werkbank. */}
        {state === "draft" && (
          <Button
            size="sm"
            variant="outline"
            disabled={submit.isPending}
            onClick={() =>
              submit.mutate(
                { repoId, branch, title },
                {
                  onSuccess: () => toast.success(`„${title}" eingereicht — wartet jetzt auf Freigabe.`),
                  onError: (err) => toast.error(repoErrorMessage(err, "Einreichen fehlgeschlagen")),
                },
              )
            }
            data-testid={testId ? `${testId}-submit` : undefined}
          >
            Einreichen
          </Button>
        )}
        {state === "submitted" && canApprove && (
          <Button
            size="sm"
            disabled={approve.isPending}
            onClick={() =>
              approve.mutate(
                { repoId, branch },
                {
                  onSuccess: () => {
                    toast.success(`„${title}" freigegeben — die Änderung ist live.`);
                    onDecided?.("freigegeben");
                  },
                  // Review M2: der Weg nach vorn (Vier-Augen-Override/Schema-Fundstellen
                  // liegen in der Werkbank — sie öffnet sich über „Anzeigen").
                  onError: (err) =>
                    toast.error(`${repoErrorMessage(err, "Freigeben fehlgeschlagen")} — in der Werkbank fortsetzen.`),
                },
              )
            }
            data-testid={testId ? `${testId}-approve` : undefined}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Freigeben
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={discard.isPending}
          onClick={() => setDiscardOpen(true)}
          data-testid={testId ? `${testId}-discard` : undefined}
        >
          <Trash2 className="h-3.5 w-3.5 text-danger" />
        </Button>
      </span>

      {changesOpen && (
        <DraftChangesDialog open onOpenChange={setChangesOpen} repoId={repoId} branch={branch} title={title} />
      )}

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Änderung verwerfen"
        description={discardDescription}
        confirmText="Verwerfen"
        confirmVariant="destructive"
        isPending={discard.isPending}
        onConfirm={() =>
          discard.mutate(
            { repoId, branch },
            {
              onSuccess: () => {
                toast.success("Änderung verworfen");
                setDiscardOpen(false);
                onDecided?.("verworfen");
              },
              onError: (err) => toast.error(repoErrorMessage(err, "Verwerfen fehlgeschlagen")),
            },
          )
        }
      />
    </li>
  );
}
